import { createContext, useContext } from 'react';
import { Moon, Palmtree, Repeat, UserRound } from 'lucide-react';
import { tintFromHex } from '../../lib/calendar/tagTint';
import { formatInZone } from '../../lib/timezone';
import { eventCapabilities, KIND_LABELS, SOURCE_LABELS } from '../../lib/calendar/eventCapabilities';
import { SHORT_BLOCK_PX } from '../../lib/calendar/timeGridLayout';

/**
 * The calendar's event chip — the ONE component every view draws an event
 * with (#679: "restyle once, keep it shared"). Despite the name it has nothing
 * to do with the tag vocabulary; it is the day-cell pill.
 *
 *   variant="chip"  — Month cells, the all-day band, the overflow popover
 *   variant="block" — a positioned block on the Week/Day time grid; the grid
 *                     owns geometry and passes it in `style`
 *
 * Colour (#679 decision D2, built by #681): the first tag's colour takes over
 * and `color_hint` is the fallback. "First" skips tags the My calendars rail
 * has unticked, so a chip is never painted in a colour whose row is off. The
 * prototype's three tints map onto the three hints the aggregator emits
 * (meeting → violet, booking → green, holiday/other → orange) and still paint
 * every untagged event. Colour is never the only signal (D2a): bookings, holidays, OOO and
 * recurring instances carry a glyph, and every chip's accessible name states
 * its kind and source.
 *
 * Draggability comes from eventCapabilities — booking_link chips and recurring
 * instances are never draggable.
 */
export const TINTS = {
    blue: { bg: '#EFEAFE', ink: '#3B0DBE', edge: '#DDD2FC' },
    emerald: { bg: '#F0FDF4', ink: '#046C4A', edge: '#CDEEDB' },
    rose: { bg: '#FFF7ED', ink: '#9A3412', edge: '#F7E0C9' },
    amber: { bg: '#FFFBEB', ink: '#92400E', edge: '#FDE68A' },
    stone: { bg: '#F1EFEA', ink: '#57504B', edge: '#E4E1DB' },
};

const NO_HIDDEN = new Set();

/**
 * The tag ids the My calendars rail has unticked (#681), for chips drawn
 * inside the calendar page. Anywhere else (the full event page) nothing is
 * hidden, so the first tag wins.
 */
export const HiddenTagsContext = createContext(NO_HIDDEN);

export function useHiddenTagIds() {
    return useContext(HiddenTagsContext);
}

/** The tag that colours an event: its first tag the rail has not hidden. */
export function colourTag(event, hiddenTagIds = NO_HIDDEN) {
    return (event?.tags || []).find(t => t && !hiddenTagIds.has(t.tag_id)) || null;
}

export function eventTint(event, hiddenTagIds = NO_HIDDEN) {
    if (event?.status === 'cancelled') return TINTS.stone;
    const fromTag = tintFromHex(colourTag(event, hiddenTagIds)?.color);
    return fromTag || TINTS[event?.color_hint] || TINTS.blue;
}

const KIND_GLYPH = { booking: UserRound, holiday: Palmtree, out_of_office: Moon };

/** Short time label ("9 AM", "9:30 AM") in the display zone, prototype style. */
export function shortTime(instant, zone) {
    const label = formatInZone(instant, { zone, formatStr: 'h:mm a' });
    return label.replace(':00 ', ' ');
}

/** Who/where line for a block (prototype `meta`). */
function metaOf(event) {
    if (event.location) return event.location;
    const first = (event.attendees || []).find(a => a && (a.name || a.email) && a.email !== event.organizer_email);
    return first ? (first.name || first.email) : '';
}

export function eventAriaLabel(event, zone) {
    const when = event.all_day
        ? 'all day'
        : `${formatInZone(event.start, { zone, formatStr: 'h:mm a' })} to ${formatInZone(event.end, { zone, formatStr: 'h:mm a' })}`;
    const tagNames = (event.tags || []).map(t => t?.name).filter(Boolean);
    const parts = [
        event.title || '(No title)', KIND_LABELS[event.kind] || event.kind, SOURCE_LABELS[event.source], when,
        tagNames.length ? `tagged ${tagNames.join(', ')}` : '',
    ];
    if (event.is_recurring_instance) parts.push('recurring');
    if (event.status === 'cancelled') parts.push('cancelled');
    return parts.filter(Boolean).join(', ');
}

export function EventTagChip({
    event, onClick, variant = 'chip', draggable = false, onDragStart, displayZone, style, heightPx, dimmed = false,
}) {
    const tint = eventTint(event, useHiddenTagIds());
    const Glyph = KIND_GLYPH[event.kind];
    const { canDrag } = eventCapabilities(event);
    const isDraggable = draggable && canDrag;
    const cancelled = event.status === 'cancelled';
    const time = event.all_day ? '' : shortTime(event.start, displayZone);

    const dragProps = isDraggable ? {
        draggable: true,
        onDragStart: (e) => {
            e.dataTransfer.setData('application/json', JSON.stringify({
                eventId: event.event_id,
                source: event.source,
                kind: event.kind,
                duration: new Date(event.end) - new Date(event.start),
            }));
            e.dataTransfer.effectAllowed = 'move';
            onDragStart?.(event);
        },
    } : { draggable: false };

    const common = {
        type: 'button',
        onClick: (e) => { e.stopPropagation(); onClick?.(event); },
        'data-testid': `event-chip-${event.event_id}`,
        'aria-label': eventAriaLabel(event, displayZone),
        title: event.title,
        ...dragProps,
    };
    const glyph = (Glyph || event.is_recurring_instance) && (
        <span className="inline-flex items-center gap-0.5 shrink-0" aria-hidden="true">
            {Glyph && <Glyph className="w-3 h-3" />}
            {event.is_recurring_instance && <Repeat className="w-2.5 h-2.5 opacity-70" />}
        </span>
    );

    if (variant === 'block') {
        const short = (heightPx ?? SHORT_BLOCK_PX) < SHORT_BLOCK_PX;
        const meta = metaOf(event);
        return (
            <button
                {...common}
                className={`absolute box-border overflow-hidden rounded-[7px] text-left ${short ? 'px-[7px] py-[3px]' : 'px-[7px] py-1'} ${isDraggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#4D11F6] focus-visible:z-10`}
                style={{
                    ...style,
                    background: tint.bg,
                    color: tint.ink,
                    borderLeft: `2px solid ${tint.ink}`,
                    boxShadow: `inset 0 0 0 1px ${tint.edge}`,
                }}
            >
                <div className={`flex items-center gap-1 text-[11.5px] font-semibold leading-[1.3] truncate ${cancelled ? 'line-through' : ''}`}>
                    {glyph}
                    <span className="truncate">{event.title || '(No title)'}</span>
                    {short && time && <span className="shrink-0 font-medium opacity-[.85]">{time}</span>}
                </div>
                {!short && (
                    <div className="text-[10.5px] opacity-[.85] leading-[1.4] truncate">
                        {[time, meta].filter(Boolean).join(' · ')}
                    </div>
                )}
            </button>
        );
    }

    return (
        <button
            {...common}
            className={`w-full flex items-center gap-1 rounded-[5px] px-1.5 py-0.5 text-left text-[10.5px] font-medium leading-[1.45] ${isDraggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#4D11F6]`}
            style={{
                ...style,
                background: tint.bg,
                color: tint.ink,
                boxShadow: `inset 0 0 0 1px ${tint.edge}`,
                opacity: dimmed ? 0.6 : undefined,
            }}
        >
            {glyph}
            {time && <span className="shrink-0">{time}</span>}
            <span className={`truncate ${cancelled ? 'line-through' : ''}`}>{event.title || '(No title)'}</span>
        </button>
    );
}
